import React, { useState, useRef, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Upload, Table, Button, message, Typography, Spin, Progress, Tooltip, Tag, Modal, Select } from "antd";
import { InboxOutlined, DeleteOutlined, PlusOutlined, ArrowRightOutlined, FileOutlined } from "@ant-design/icons";
import TemplatePrompt from "./TemplatePrompt";
import { ICFContext } from "./ICFcontext";
import { uploadICFFile, digitizeICFFile, getICFDigitizationPercent, fileToBase64, getAllProtocolVersions, addAttributes } from "../../../services/icfService";
import { useAuth } from "../../contexts/AuthContext";
import { metadataOptions } from "../../../../constant";
import SessionHistoryTable from "./SessionHistoryTable";

const { Dragger } = Upload;
const { Text, Title } = Typography;

// Global font
const GlobalStyle = createGlobalStyle`
  * {
    font-family: 'Open Sans', sans-serif !important;
  }
`;

// Styled button
const StyledButton = styled(Button)`
  background: ${({ danger }) => (danger ? "#ffe5e5" : "#e3ecff")};
  color: ${({ danger }) => (danger ? "#d93025" : "#426bba")} !important;
  font-weight: 600;
  font-size: 14px;
  border: none;
  border-radius: 1rem;
  box-shadow: 0 2px 6px rgba(66, 107, 186, 0.2);
  padding: 4px 14px;

  &:hover {
    background: ${({ danger }) => (danger ? "#ffd6d6" : "#d7e4ff")} !important;
    color: ${({ danger }) => (danger ? "#d93025" : "#426bba")} !important;
  }
`;

// Styled table wrapper
export const StyledTableWrapper = styled.div`
  .ant-table-thead > tr > th {
    background: #426bba !important;
    border: 1px solid #426bba;
    color: #fefeff !important;
    font-weight: 600;
    font-size: 12px;
    line-height: 1.2;
  }

  .ant-table-tbody > tr > td {
    background: linear-gradient(
      150deg,
      rgba(235, 240, 255, 1) 25%,
      rgba(225, 235, 255, 1) 50%,
      rgba(215, 230, 255, 1) 100%
    );
    border: 1px solid #bfd3f2;
    color: #2c4c91;
    font-size: 11px;
    padding: 6px 12px;
    line-height: 1;
  }
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  height: 28px;
  font-size: 12px;
  font-weight: 500;
  color: #426bba;
  border-radius: 0.75rem !important;
  box-shadow: 0 1px 4px rgba(66, 107, 186, 0.2);
  text-align: center;

  && .ant-select-selector {
    background-color: #e3ecff !important;
    font-size: 12px;
    font-weight: 500;
    border: none !important;
    border-radius: 0.75rem !important;
    padding: 2px 12px;
    color: #426bba !important;
    text-align: center;
  }

  && .ant-select-selection-item,
  && .ant-select-selection-placeholder {
    font-size: 12px;
    font-weight: 500;
    color: #426bba !important;
    text-align: center;
  }

  && .ant-select-arrow {
    color: #426bba !important;

    svg {
      fill: #426bba !important;
    }
  }

  && .ant-select-dropdown {
    background-color: #f8faff;
    border-radius: 0.75rem;
  }

  && .ant-select-item-option-content {
    font-size: 12px;
    font-weight: 500;
    color: #426bba;
  }

  &:hover .ant-select-selector {
    background-color: #d7e4ff !important;
    color: #426bba !important;
  }
`;

const FileNameTag = ({ fileName, showIconOnly = false }) => {
  return (
    <Tooltip title={fileName}>
      <Tag
        color="blue"
        style={{
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          cursor: "pointer",
        }}
      >
        <FileOutlined />
        {!showIconOnly && (
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {fileName}
          </span>
        )}
      </Tag>
    </Tooltip>
  );
};

const ICFUnified = ({ onBack }) => {
  const [fileList, setFileList] = useState([]);
  const [protocolId, setProtocolId] = useState(null);
  const [metadata, setMetadata] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newAttr, setNewAttr] = useState(null);
  const [newVal, setNewVal] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [progress, setProgress] = useState(null);
  const [tip, setTip] = useState("Loading, please wait...");
  const [loading, setLoading] = useState(false);
  const [docVersion, setDocVersion] = useState("");
  const [jobId, setJobId] = useState("");
  const [indication, setIndication] = useState("");
  const [productName, setProductName] = useState("");
  const [procedures, setProcedures] = useState([]);
  const [statusCode, setStatusCode] = useState(null);
  const [protocols, setProtocols] = useState([])
  const [existingVersion, setExistingVersion] = useState(false)
  const [docId, setDocId] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [jobIdLogAPI, setJobIdLogAPI] = useState(null);
  const [fileIdLogAPI, setFileIdLogAPI] = useState(null);

  const { user } = useAuth();
  const userEmail = user.username || "";
  const userId = user?.username || "";
  // console.log(user);

  const pollIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);

  const abortControllerHandle = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // cancels the request
    }
  };

  const clearIntervalHandle = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
  };

  // delete with confirmation
  const handleDelete = (file) => {
    Modal.confirm({
      title: "Delete File",
      content: `Are you sure you want to delete "${file.name}"?`,
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        abortControllerHandle();
        clearIntervalHandle();
        setProtocolId(null);
        setJobIdLogAPI(null)
        setFileIdLogAPI(null)
        setDocId(null)
        setFileList([]);
        setUploadSuccess(false);
        setMetadata([]);
        setExistingVersion(false)
      },
    });
  };

  // Poll digitization status
  const pollDigitizationProgress = (file_path) => {
    // clear old interval first
    // if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    clearIntervalHandle();

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await getICFDigitizationPercent({ file_path });
        const percent = parseInt(res?.data?.digitization_percent, 10) || 0;
        setProgress(percent);
        setTip(res.message);

        if (percent >= 90) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          message.success("Digitization complete!");
        }
      } catch (err) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        if (err?.response?.status === 504) {
          message.error("Server timed out. Please try again.");
        } else {
          message.error("Failed to fetch digitization progress.");
        }
        setProgress(null);
        onComplete?.();
      }
    }, 6000);
  };

  // Upload handling with API
  const handleUpload = async () => {
    if (!fileList.length) {
      message.warning("Please select a file to upload.");
      return;
    }
    const file = fileList[0];
    const file_name = file.name;
    const base64File = await fileToBase64(file);
    const file_path = "/" + file_name;

    const payload = {
      file_b64: base64File,
      file_name: file.name,
    };
    // console.log("Payload Data", payload);

    try {
      setLoading(true);
      const response = await uploadICFFile(payload, {
        updated_by: userEmail
      });
      // console.log("Upload API response", response);
      message.success(response.message);

      setTip(response.message);

      // const { protocol_id, file_path } = response;
      const { document_id, job_id, file_id } = response.data || {};

      setDocId(document_id);
      setJobIdLogAPI(job_id)
      setFileIdLogAPI(file_id)
      setUploadSuccess(true);
      setTip("File is being digitized...");
      // create a fresh controller
      abortControllerRef.current = new AbortController();

      digitizeICFFile(
        {
          file_name,
          created_by: userEmail,
          user_id: userId,
          log_job_id: job_id,
          file_id: file_id,
        },
        abortControllerRef.current.signal,
        {
          job_id: job_id,
          file_id: file_id,
          updated_by: userEmail
        }
      )
        .then((res) => {
          // console.log("Digitization Response", res?.data);
          setStatusCode(res?.status_code);
          setDocVersion(res?.data?.doc_version);
          setJobId(res?.data?.job_id);

          const apiMetadata = res?.data?.metadata || {};

          console.log("apiMetadata: ", apiMetadata)
          
          setIndication(apiMetadata?.indication);
          setProductName(apiMetadata?.product_name);
          setProcedures(apiMetadata?.procedures);
          setMetadata([
            { key: "Protocol ID", value: apiMetadata?.protocol_id, deletable: false },
            { key: "Indication", value: apiMetadata?.indication || "N/A", deletable: false },
            { key: "Product Name", value: apiMetadata?.product_name || "N/A", deletable: false },
          ]);
          setProtocolId(apiMetadata?.protocol_id);
          setLoading(false);
          setProgress(null);
        })
        .catch((err) => {
          console.log(err);
          if (err?.response?.status === 504) {
            message.error("Server timed out. Please try again.");
          } else {
            message.error("Digitization Failed.");
          }
          setLoading(false);
          setProgress(null);
          setProtocolId(null);
          setJobIdLogAPI(null)
          setFileIdLogAPI(null)
          setDocId(null)
          setStatusCode(err?.code);
          // stop polling if digitization fails
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        });
      pollDigitizationProgress(file_path);
    } catch (error) {
      setLoading(false);
      console.log("inside outer Catch", error);
      message.error("Upload or digitization failed.");
    }
  };

  const handleDeleteMetadata = (index) => {
    setMetadata((prev) => prev.filter((_, i) => i !== index));
  };

  const getAttributeTag = (key) => metadataOptions.find((opt) => opt.attributeValue === key)?.attributeTag || key;

  const getValueLabel = (key, value) => {
    const opt = metadataOptions.find((opt) => opt.attributeValue === key);
    return opt?.values.find((v) => String(v.value) === String(value))?.label || value;
  };

  const draggerProps = {
    multiple: false,
    maxCount: 1,
    beforeUpload: (file) => {
      // Restrict file types (only PDF & DOCX) — accept attribute alone
      // doesn't reliably block drag-and-drop or all file pickers.
      const allowedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const allowedExtensions = [".pdf", ".docx"];

      const fileExt = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      const isAllowedType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExt);

      if (!isAllowedType) {
        Modal.error({
          title: "Invalid File Type",
          content: "Only PDF and DOCX files are allowed.",
        });
        return Upload.LIST_IGNORE; // reject
      }

      setFileList([file]);
      setUploadSuccess(false);
      setMetadata([]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
      setUploadSuccess(false);
      setMetadata([]);
    },
    fileList,
    accept:
      ".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  };

  const handleBack = () => {
    const isFileListEmpty = fileList.length === 0;
    const modalTitle = isFileListEmpty ? "Are you sure ?" : "Discard Changes?";
    const modalContent = isFileListEmpty
      ? "Do you want to go back?"
      : "Your file will be discarded if you go back. Are you sure?";

    Modal.confirm({
      title: modalTitle,
      content: modalContent,
      okText: "Yes, Go Back",
      cancelText: "Stay",
      okType: "danger",
      onOk() {
        onBack?.();
        abortControllerHandle();
        clearIntervalHandle();
        setProtocolId(null);
        setJobIdLogAPI(null)
        setFileIdLogAPI(null)
        setDocId(null)
        setFileList([]);
        setUploadSuccess(false);
        setMetadata([]);
        setExistingVersion(false)
      },
      onCancel() {
        console.log("Stay on page");
      },
    });
  };

  const getDocId = (filename) => {
    const withoutExt = filename.replace(/\.[^/.]+$/, "");
    return withoutExt
  }

  const onClickProtocolVersion = async protocol => {
    const protocolName = protocol?.protocol
    setShowTemplate(true)
    setExistingVersion(true)
    setDocId(getDocId(protocolName))
    setProtocolId(protocol?.protocol_id)
    setDocVersion(protocol?.protocol_version)
    setJobIdLogAPI(protocol?.log_job_id)
    setFileIdLogAPI(protocol?.file_id)
    setFileList([{ name: protocolName }])
    setJobId(protocol?.job_ids?.[0]);
    const apiMetadata = protocol?.metadata || {};
    setIndication(apiMetadata?.indication);
    setProductName(apiMetadata?.product_name);
    setProcedures(apiMetadata?.procedures);
    setMetadata([
      { key: "Protocol ID", value: apiMetadata?.protocol_id, deletable: false },
      { key: "Indication", value: apiMetadata?.indication || "N/A", deletable: false },
      { key: "Product Name", value: apiMetadata?.product_name || "N/A", deletable: false },
    ]);
  }

  const fetchProtocolVersions = () => {
    setLoadingVersion(true)
    getAllProtocolVersions({ user_id: userId })
      .then(res => setProtocols(res.response))
      .catch((err) => console.log(err))
      .finally(() => setLoadingVersion(false))
  }

  useEffect(() => {
    fetchProtocolVersions()
  }, [])

  if (showTemplate) {
    return (
      <TemplatePrompt
        protocolId={protocolId}
        docId={docId}
        jobId={jobId}
        docVersion={docVersion}
        // indication={indication}
        productName={productName}
        userMetadata={metadata}
        procedures={procedures}
        userEmail={userEmail}
        userId={userId}
        onBack={() => {
          abortControllerHandle();
          clearIntervalHandle();
          setProtocolId(null);
          setJobIdLogAPI(null)
          setFileIdLogAPI(null)
          setDocId(null)
          setFileList([]);
          setUploadSuccess(false);
          setMetadata([]);
          setShowTemplate(false)
          setExistingVersion(false)
          fetchProtocolVersions()
        }}
        fileList={fileList}
        existingVersion={existingVersion}
        jobIdLogAPI={jobIdLogAPI}
        fileIdLogAPI={fileIdLogAPI}
      />
    );
  }

  const icfContextValue = {
    fileList,
    setFileList,
    handleUpload,
    metadata,
    setMetadata,
    progress,
    protocolId,
    uploadSuccess,
    setShowTemplate,
  };

  const isBoldAttribute = (key) => true;

  return (
    <ICFContext.Provider value={icfContextValue}>
      <div style={{ padding: "12px" }}>
        <Spin
          spinning={loading}
          tip={
            progress > 0 && progress < 100 ? (
              <div style={{ textAlign: "center" }}>
                <div style={{ marginBottom: 8 }}>{tip}</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: "70%" }}>
                    <Progress percent={progress} size="small" />
                  </div>
                </div>
              </div>
            ) : (
              tip
            )
          }
        >
          <GlobalStyle />
          {/* Top Bar */}

          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              marginBottom: "8px",
              gap: "2rem",
            }}
          >
            <StyledButton onClick={handleBack} size="small">
              Back
            </StyledButton>
            {fileList?.[0]?.name && <FileNameTag fileName={fileList?.[0]?.name} />}
          </div>

          <Title level={5} style={{ color: "#426bba", lineHeight: 1.4 }}>
            <span style={{ fontSize: "17px", fontWeight: 600 }}>For 'ICF' Deliverable,</span> <br />
            <span style={{ fontSize: "19px", fontWeight: 800 }}>Choose a Protocol:</span>
          </Title>

          <Dragger {...draggerProps} fileList={fileList} showUploadList={false}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "#426BBA" }} />
            </p>
            <p className="ant-upload-text">Click or Drag files here to upload</p>
            <p className="ant-upload-hint">Supports PDF/DOCX file</p>
          </Dragger>

          {fileList.length > 0 && (
            <div style={{ marginTop: "10px" }}>
              <h6 style={{ fontSize: 11, margin: "5px 0px" }}>Selected File:</h6>
              {fileList.map((file) => (
                <span key={file.uid} style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
                  <span style={{ flex: 1 }}>{file.name}</span>
                  <DeleteOutlined style={{ color: "red", cursor: "pointer" }} onClick={() => handleDelete(file)} />
                </span>
              ))}
            </div>
          )}

          {uploadSuccess && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  marginTop: "16px",
                  marginBottom: "8px",
                }}
              >
                {!isAdding && (
                  <Text
                    style={{
                      fontSize: "11px",
                      color: "#426bba",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                    onClick={() => setIsAdding(true)}
                  >
                    <PlusOutlined /> Add Attribute
                  </Text>
                )}
              </div>

              <StyledTableWrapper>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={[
                    ...metadata.map((m, i) => ({
                      key: i,
                      attribute: getAttributeTag(m.key),
                      value: (
                        <span
                          style={{
                            fontWeight: isBoldAttribute(m.key) ? 900 : "normal",
                          }}
                        >
                          {getValueLabel(m.key, m.value)}
                        </span>
                      ),
                      action: m.deletable ? (
                        <Button
                          icon={<DeleteOutlined />}
                          size="small"
                          type="text"
                          danger
                          onClick={() => handleDeleteMetadata(i)}
                        />
                      ) : null,
                    })),
                    isAdding && {
                      key: "new",
                      attribute: (
                        <StyledSelect
                          size="small"
                          placeholder="Select Attribute"
                          value={newAttr}
                          onChange={(value) => {
                            setNewAttr(value);
                            setNewVal([]);
                          }}
                          // style={{
                          //   width: "100%",
                          //   maxWidth: "100%",
                          //   overflow: "hidden",
                          //   textOverflow: "ellipsis",
                          // }}
                          dropdownStyle={{ maxWidth: 300, whiteSpace: "normal", wordWrap: "break-word" }}
                        >
                          {metadataOptions
                            .filter((opt) => !metadata.some((m) => m.key === opt.attributeValue))
                            .map((opt) => (
                              <StyledSelect.Option key={opt.attributeTag} value={opt.attributeValue}>
                                {opt.attributeTag}
                              </StyledSelect.Option>
                            ))}
                        </StyledSelect>
                      ),
                      value: (
                        <StyledSelect
                          mode="single"
                          size="small"
                          placeholder="Select Value"
                          value={newVal}
                          onChange={(value) => setNewVal(value)}
                          disabled={!newAttr}
                          // style={{
                          //   width: "100%",
                          //   maxWidth: "100%",
                          //   overflow: "hidden",
                          //   textOverflow: "ellipsis",
                          // }}
                          dropdownStyle={{ maxWidth: 300, whiteSpace: "normal", wordWrap: "break-word" }}
                        >
                          {metadataOptions
                            .find((opt) => opt.attributeValue === newAttr)
                            ?.values.map((val) => (
                              <StyledSelect.Option key={val.label} value={val.value}>
                                {val.label}
                              </StyledSelect.Option>
                            ))}
                        </StyledSelect>
                      ),
                    },
                  ].filter(Boolean)}
                  columns={[
                    {
                      title: "Attribute",
                      dataIndex: "attribute",
                      key: "attribute",
                      width: "40%",
                      ellipsis: true,
                    },
                    {
                      title: "Value",
                      dataIndex: "value",
                      key: "value",
                      width: "50%",
                      onCell: () => ({
                        style: {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        },
                      }),
                    },
                    ...(metadata.some((m) => m.deletable)
                      ? [
                        {
                          title: "",
                          dataIndex: "action",
                          key: "action",
                          width: "10%",
                          align: "center",
                        },
                      ]
                      : []),
                  ]}
                />
              </StyledTableWrapper>

              {/* Add/Cancel buttons at bottom-right */}
              {isAdding && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    marginTop: "12px",
                    marginBottom: "35px",
                    gap: "8px",
                  }}
                >
                  <StyledButton
                    size="small"
                    onClick={async () => {
                      if (newAttr && newVal !== "") {
                        const payload = {
                          protocol_id: protocolId,
                          protocol_version: "v1",
                          user_id: userId,
                          [newAttr]: newVal
                        };

                        try {
                          await addAttributes(payload);
                          setMetadata((prev) => [...prev, { key: newAttr, value: newVal, deletable: true }]);
                          setNewAttr("");
                          setNewVal([]);
                          setIsAdding(false);
                        } catch (err) {
                          console.error("Failed to add attributes", err);
                        }
                      }
                    }}
                  >
                    Add
                  </StyledButton>
                  <StyledButton
                    size="small"
                    onClick={() => {
                      setNewAttr("");
                      setNewVal([]);
                      setIsAdding(false);
                    }}
                    danger
                  >
                    Cancel
                  </StyledButton>
                </div>
              )}
            </>
          )}

          <div style={{ display: "flex", justifyContent: "center", marginTop: 20, marginBottom: 30 }}>
            {uploadSuccess ? (
              <Tooltip
                title={
                  statusCode !== 200
                    ? "Digitization failed. Please retry."
                    : !indication || !productName || !procedures
                      ? "Digitization failed. Missing Indication, Product Name, or Procedures."
                      : ""
                }
              >
                <StyledButton
                  type="primary"
                  onClick={() => setShowTemplate(true)}
                  icon={<ArrowRightOutlined />}
                  disabled={statusCode !== 200 || !indication || !productName || !procedures || isAdding}
                >
                  Next
                </StyledButton>
              </Tooltip>
            ) : (
              <StyledButton type="primary" onClick={handleUpload}>
                Upload Protocol
              </StyledButton>
            )}
          </div>
          {!uploadSuccess && (<SessionHistoryTable protocols={protocols} loading={loadingVersion} onSelectSession={onClickProtocolVersion} />)}

        </Spin>
      </div>
    </ICFContext.Provider>
  );
};

export default ICFUnified;
